import { createElement, Fragment } from 'react'

import {
  getAttributesFromExtensions,
  resolveExtensions,
  splitExtensions,
} from '@tiptap/core'
import {
  type Mark as ProseMirrorMark,
  type Node as ProseMirrorNode,
} from '@tiptap/pm/model'
import {
  generateJSON,
  generateText,
  type JSONContent,
  type ReactNodeViewProps,
} from '@tiptap/react'
import {
  domOutputSpecToReactElement,
  mapMarkExtensionToReactNode,
  mapNodeExtensionToReactNode,
  renderToHTMLString,
  renderToReactElement,
  type NodeProps,
  type TiptapStaticRendererOptions,
} from '@tiptap/static-renderer'

import * as logger from '@/utils/logger'

import { FILENODE_NAME } from '~shared/richtext/nodes/file.node'
import { IMAGENODE_NAME } from '~shared/richtext/nodes/image.node'
import { MENTIONSNODE_NAME } from '~shared/richtext/nodes/mention.node'
import { TASKITEM_NAME } from '~shared/richtext/nodes/taskitem.node'
import { VARIABLENODE_NAME } from '~shared/richtext/nodes/variable.node'
import { EMPTY_PARAGRAPH_HTML } from '~shared/richtext/richtext.types'
import { EMPTY_PARAGRAPH_JSON } from '~shared/richtext/richtext.types'
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { type cellHtmlToPlainText } from '~shared/utils'

import { FileNodeViewStatic } from './nodes/file/FileNodeView'
import { ImageNodeViewStatic } from './nodes/image/ImageNodeView'
import { MentionNodeViewStatic } from './nodes/mentions/MentionNodeView'
import { TaskItemNodeViewStatic } from './nodes/taskitem/TaskItemNodeView'
import { VariableNodeViewStatic } from './nodes/variables/VariableNodeView'
import { CLIENT_RICHTEXT_EXTENSIONS } from './richtext.extensions'

/**
 * Helper type for NodeView's that need to be rendered in the editor.
 */
export type NodeViewLiveProps<T extends HTMLElement = HTMLDivElement> =
  ReactNodeViewProps<T>

/**
 * Helper type for NodeView's that need to be statically rendered.
 * They must be used in `nodeMapping`, which provides different types
 * than the live `ReactNodeViewRenderer` function.
 */
export type NodeViewStaticProps = NodeProps<ProseMirrorNode>

type StaticReactOptions = Partial<
  TiptapStaticRendererOptions<React.ReactNode, ProseMirrorMark, ProseMirrorNode>
>

/**
 * Helper type for the node component that will be rendered either
 * statically or in a live editor.
 */
export type NodeComponentProps = {
  node: { attrs: ProseMirrorNode['attrs'] }
}

export const TIPTAP_CLASSES = {
  BASE: 'tiptap ProseMirror',
  STATIC: 'tiptap-static',
  LIVE: 'tiptap-live',
} as const

/**
 * Generates Tiptap JSON doc from HTML string.
 *
 * @note Safe: If content is undefined or empty, returns doc with empty paragraph.
 */
export function createJSONContentFromHTMLContent(contentAsHTML: string) {
  return generateJSON(
    contentAsHTML || EMPTY_PARAGRAPH_HTML, // Safe fallback
    CLIENT_RICHTEXT_EXTENSIONS,
  )
}

export function renderJSONContentAsHTMLString(contentAsJSON: JSONContent) {
  return renderToHTMLString({
    content: contentAsJSON || EMPTY_PARAGRAPH_JSON, // Safe fallback
    extensions: CLIENT_RICHTEXT_EXTENSIONS,
  })
}

/**
 * Turns tiptap JSON content into plaintext.
 *
 * This serializer follows the conventions documented in
 * {@link cellHtmlToPlainText}() Our custom nodes are serialized using their
 * `renderText()` methods, while serialiation for built-in nodes can be
 * configured below.
 */
export function renderJSONContentAsPlaintext(contentAsJSON: JSONContent) {
  return generateText(
    contentAsJSON || EMPTY_PARAGRAPH_JSON, // Safe fallback
    CLIENT_RICHTEXT_EXTENSIONS,
    {
      // Use one break, instead of default two
      blockSeparator: '\n',
      // Customize serialization
      textSerializers: {
        text: ({ node }) => {
          // Check if node contains link marks
          if (node.marks.some((m) => m.type.name === 'link')) {
            const linkMark = node.marks.find((m) => m.type.name === 'link')

            const href = linkMark?.attrs.href

            if (node.text && node.text === href) {
              return node.text // Skip if text and href are the same
            }

            return `${node.text} [${href}]` // Serialize as `link [href]`
          }

          return node.text || '' // Skip
        },
      },
    },
  )
}

const STATIC_REACT_OPTIONS = {
  // Warn about unknown marks and nodes
  unhandledMark: (ctx) => {
    logger.warn('[Preview] unhandledMark', ctx)
  },
  unhandledNode: (ctx) => {
    logger.warn('[Preview] unhandledNode', ctx)
  },
} satisfies StaticReactOptions

/**
 * Node and mark components for the static React renderer, created once.
 *
 * `renderToReactElement()` creates new components for every `renderHTML()`
 * node and mark on each call. React then sees new component types and
 * remounts the whole tree on every render, which loses focus and detaches
 * event targets mid-click. Passing our own mappings for all types skips that,
 * because user mappings take precedence. Mirrors `renderToElement()` in
 * `@tiptap/static-renderer`, review on upgrades. Don't pass
 * `staticEditorOptions` to the renderer: they can add extensions this mapping
 * lacks, which brings the remounts back.
 */
const STATIC_REACT_MAPPINGS = (() => {
  const extensions = resolveExtensions(CLIENT_RICHTEXT_EXTENSIONS)
  const attributes = getAttributesFromExtensions(extensions)
  const { nodeExtensions, markExtensions } = splitExtensions(extensions)

  const nodeMapping: NonNullable<StaticReactOptions['nodeMapping']> = {
    ...Object.fromEntries(
      nodeExtensions.map((extension) =>
        mapNodeExtensionToReactNode<React.ReactNode>(
          domOutputSpecToReactElement,
          extension,
          attributes,
          STATIC_REACT_OPTIONS,
        ),
      ),
    ),
    // Same as the defaults in `renderToReactElement()`
    doc: StaticDocNode,
    text: StaticTextNode,
    /**
     * This mapping is required for custom nodes that use
     * `ReactNodeViewRenderer`. If the node is listed in `extensions`, but not
     * mapped it wil fall back to `renderHTML()`.
     *
     * See:
     * https://tiptap.dev/docs/editor/api/utilities/static-renderer#react-nodeviews
     */
    [IMAGENODE_NAME]: ImageNodeViewStatic,
    [FILENODE_NAME]: FileNodeViewStatic,
    [VARIABLENODE_NAME]: VariableNodeViewStatic,
    [TASKITEM_NAME]: TaskItemNodeViewStatic,
    [MENTIONSNODE_NAME]: MentionNodeViewStatic,
  }

  const markMapping: NonNullable<StaticReactOptions['markMapping']> =
    Object.fromEntries(
      markExtensions.map((extension) =>
        mapMarkExtensionToReactNode<React.ReactNode>(
          domOutputSpecToReactElement,
          extension,
          attributes,
          STATIC_REACT_OPTIONS,
        ),
      ),
    )

  return { nodeMapping, markMapping }
})()

function StaticDocNode({ children }: { children?: React.ReactNode }) {
  return createElement(Fragment, null, children)
}

function StaticTextNode({ node }: NodeViewStaticProps) {
  return node.text ?? ''
}

export function renderJSONContentAsReactNode(contentAsJSON: JSONContent) {
  return renderToReactElement({
    extensions: CLIENT_RICHTEXT_EXTENSIONS,
    content: contentAsJSON,
    options: {
      ...STATIC_REACT_OPTIONS,
      ...STATIC_REACT_MAPPINGS,
    },
  })
}
